import React, { Suspense } from 'react';
import { Center, Loading } from '@cotera/client/app/components';
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  useOpenProviderModal,
  useProviderAccounts,
} from '@cotera/client/v0/components/tool-connections/hooks';
import { ToolConnectionStatusCard } from '@cotera/client/v0/components/tool-connections/tool-connection-status';
import { ProviderAccountCards } from '@cotera/client/v0/components/tool-connections/provider-account-cards';
import { WebhookProviderIcon } from '../webhook-provider-icons';
import type { WebhookProviderCatalogEntry } from '../types';
import {
  requiresWebhookProviderToolConnection,
  resolveWebhookToolProviderKey,
  webhookProviderConnectionDescription,
} from './webhook-provider-tool-connection';

const WebhookProviderConnectionStatusInner: React.FC<{
  catalog: WebhookProviderCatalogEntry;
  toolProviderKey: string;
}> = ({ catalog, toolProviderKey }) => {
  const openProviderModal = useOpenProviderModal();
  const { data: accountsData } = useProviderAccounts(toolProviderKey);

  const accounts = accountsData.accounts;

  // Key the connected/disconnected state off the account list (like the tool
  // settings modal), NOT `configStatus`. When there are account(s), render the
  // exact same per-account cards — with inline delete — so the two surfaces
  // look and behave identically. When there are none (including right after a
  // delete), always offer "Connect" so the user can re-add credentials;
  // `configStatus` can lag or stay `configured`, which would otherwise leave
  // the card stuck with no way to reconnect.
  if (accounts.length > 0) {
    return (
      <ProviderAccountCards provider={toolProviderKey} accounts={accounts} />
    );
  }

  return (
    <ToolConnectionStatusCard
      title={`${catalog.displayName} connection`}
      description={webhookProviderConnectionDescription(catalog)}
      icon={
        <WebhookProviderIcon providerKey={catalog.key} className="h-4 w-4" />
      }
      status="not-connected"
      action={
        <Button
          type="button"
          size="sm"
          onClick={() => openProviderModal(toolProviderKey)}
        >
          Connect {catalog.displayName}
        </Button>
      }
    />
  );
};

export const WebhookProviderConnectionStatus: React.FC<{
  catalog: WebhookProviderCatalogEntry;
}> = ({ catalog }) => {
  const toolProviderKey = resolveWebhookToolProviderKey(catalog);
  if (
    !requiresWebhookProviderToolConnection(catalog) ||
    toolProviderKey === null
  ) {
    return null;
  }

  return (
    <Suspense
      fallback={
        <div className="rounded-md border bg-muted/20 p-3">
          <Center className="py-2">
            <Loading.Dots />
          </Center>
        </div>
      }
    >
      <WebhookProviderConnectionStatusInner
        catalog={catalog}
        toolProviderKey={toolProviderKey}
      />
    </Suspense>
  );
};
